// Import React hooks for state and lifecycle management
import { useState, useEffect } from 'react';
// React Router hook for programmatic navigation
import { useNavigate } from 'react-router-dom';
// Custom hook to access authenticated user
import { useAuth } from '../hooks/useAuth';
// Axios instance for API requests
import axiosClient from '../api/axiosClient';
// Type definition for Course
import type { Course } from '../types';

// Component for students to provide feedback on courses
function ProvideFeedbackPage() {
  // Get current user and loading state
  const { user, isLoading } = useAuth();
  // State for list of courses available to provide feedback
  const [courses, setCourses] = useState<Course[]>([]);
  // State for currently selected course in the dropdown
  const [selectedCourseId, setSelectedCourseId] = useState<string>('');
  // State for the feedback content
  const [content, setContent] = useState('');
  // State for displaying error messages
  const [error, setError] = useState<string | null>(null);
  // React Router hook for navigation
  const navigate = useNavigate();
  // Fetch courses and validate user role on component mount
  useEffect(() => {
    if (isLoading) {
      return; // Wait until auth is fully loaded
    }
    if (!user) {
      navigate('/login'); // Redirect unauthenticated users to login
      return;
    }
    if (user.role !== 'student') {
      setError('Only students can provide feedback'); // Only students can submit feedback
      return;
    }
    // Async function to fetch courses and enrollments
    const fetchCourses = async () => {
      try {
        // Fetch all courses
        const courseResponse = await axiosClient.get('/elearning/api/courses/', {
          withCredentials: true,
          headers: {
            'X-CSRFToken': document.cookie.match(/csrftoken=([^;]+)/)?.[1] || '',
          },
        });
        const coursesData = Array.isArray(courseResponse.data) ? courseResponse.data : [];
        // Fetch the courses that the student is enrolled in
        let enrolledCourseIds: number[] = [];
        try {
          const enrollmentsResponse = await axiosClient.get('/elearning/api/enrollments/', {
            withCredentials: true,
            headers: {
              'X-CSRFToken': document.cookie.match(/csrftoken=([^;]+)/)?.[1] || '',
            },
          });
          enrolledCourseIds = Array.isArray(enrollmentsResponse.data)
            ? enrollmentsResponse.data
                .filter((e: any) => e.course && (typeof e.course.id === 'number' || typeof e.course.id === 'string'))
                .map((e: any) => Number(e.course.id))
            : [];
        } catch (enrollErr: any) {
          console.error('[ProvideFeedbackPage] Enrollments error=', JSON.stringify(enrollErr.response?.data || enrollErr.message, null, 2));
          setError('Failed to load enrollments. Please try again.');
          return;
        }
        // Filter courses to only those that the student is enrolled in
        const filteredCourses = coursesData
          .filter((course: any) => {
            if (!course || !course.id) {
              console.warn('[ProvideFeedbackPage] Skipping invalid course=', JSON.stringify(course));
              return false;
            }
            return enrolledCourseIds.includes(Number(course.id));
          })
          .map((course: any) => {
            return {
              id: Number(course.id),
              name: course.name || `Course ${course.id}`,
              description: course.description || '',
              enrolled: true,
              slug: course.slug || `course-${course.id}`,
              teacher: {
                id: Number(course.teacher?.id || 0),
                username: course.teacher?.username || '',
                real_name: course.teacher?.real_name || null,
                email: course.teacher?.email || null,
                photo: course.teacher?.photo || null,
                role: course.teacher?.role || 'teacher',
              },
              materials: course.materials || null,
            };
          });
        // Update state with filtered courses
        setCourses(filteredCourses);
        if (filteredCourses.length > 0) {
          setSelectedCourseId(filteredCourses[0].id.toString()); // Default selection to first course
        } else {
          setError('No courses available for feedback. Please enroll in a course.');
        }
      } catch (courseErr: any) {
        const errorMessage = courseErr.response?.data?.error
          ? JSON.stringify(courseErr.response.data.error, null, 2)
          : courseErr.message || 'Failed to load courses. Please try again.';
        console.error('[ProvideFeedbackPage] Error fetching courses=', errorMessage);
        setError(errorMessage);
      }
    };
    // Invoke async fetch function
    fetchCourses();
  }, [isLoading, user, navigate]);
  // Handle feedback form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Validate selected course
    if (!selectedCourseId) {
      setError('Please select a course');
      return;
    }
    // Validate feedback content
    if (!content.trim()) {
      setError('Feedback cannot be empty');
      return;
    }
    try {
      // Fetch CSRF token for secure POST request
      const csrfResponse = await axiosClient.get('/elearning/get-csrf/', {
        withCredentials: true,
      });
      const csrfToken = csrfResponse.data.csrfToken;
      // Prepare payload
      const payload = {
        course_id: Number(selectedCourseId),
        content,
      };
      // Submit feedback to backend
      await axiosClient.post(
        '/elearning/api/feedback/',
        payload,
        {
          withCredentials: true,
          headers: {
            'X-CSRFToken': csrfToken,
            'Content-Type': 'application/json',
          },
        }
      );
      // Navigate back to courses page after successful submission
      navigate('/courses');
    } catch (err: any) {
      const errorMessage = err.response?.data
        ? JSON.stringify(err.response.data, null, 2)
        : err.message || 'Failed to submit feedback. Please try again.';
      console.error('[ProvideFeedbackPage] Error submitting feedback=', errorMessage);
      setError(errorMessage);
    }
  };
  // Display loading spinner while authentication is loading
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-blue-600"></div>
      </div>
    );
  }
  // Main page rendering
  return (
  <div className="min-h-screen bg-gray-50 flex items-start justify-center py-12 px-4 sm:px-6 lg:px-8">
    <div className="w-full max-w-md bg-white rounded-2xl shadow-lg p-8 border border-gray-200">
      <h1 className="text-2xl font-bold mb-6">Provide Feedback</h1>

      {/* Navigation buttons */}
      <div className="mb-6 flex gap-4">
        <button
          onClick={() => navigate(-1)}
          className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Back
        </button>
        <button
          onClick={() => navigate('/')}
          className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Back to Homepage
        </button>
      </div>

      {/* Display error messages */}
      {error && (
        <p className="text-red-500 mb-4 bg-red-50 p-3 rounded-lg shadow-sm">{error}</p>
      )}

      {/* Feedback form */}
      <form onSubmit={handleSubmit} className="flex flex-col gap-5">
        {/* Course selection */}
        {courses.length > 0 ? (
          <div>
            <label htmlFor="courseId" className="block text-sm text-gray-700 mb-1">
              Select Course
            </label>
            <select
              id="courseId"
              value={selectedCourseId}
              onChange={(e) => setSelectedCourseId(e.target.value)}
              className="w-full border p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="" disabled>
                Select a course
              </option>
              {courses.map((course) => (
                <option key={course.id} value={course.id}>
                  {course.name}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <p className="text-gray-500 mb-4">
            No courses available for feedback. Please enroll in a course.
          </p>
        )}

        {/* Feedback content */}
        <div>
          <label htmlFor="content" className="block text-sm text-gray-700 mb-1">
            Feedback
          </label>
          <textarea
            id="content"
            name="content"
            placeholder="Your feedback"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={5}
            className="w-full border p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Submit button */}
        <button
          type="submit"
          disabled={courses.length === 0}
          className={`w-full py-3 px-4 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors ${
            courses.length === 0 ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          Submit
        </button>
      </form>
    </div>
  </div>
);

}
// Export component as default
export default ProvideFeedbackPage;
